/* Routeur racine de l'application Factumis (app.factumis.fr).
   Deux zones distinctes :
   - Routes publiques : /connexion, /inscription, /onboarding, /invitation/:id…
     Aucune garde, accessibles sans session.
   - Arbre protégé (sous <RequireSession />) : session Better Auth + organisation
     active requises. Le shell <Layout /> (Sidebar + Topbar + <Outlet />) encadre
     tous les écrans métier. La route /admin est dans le shell mais hors menu
     (accès par URL directe uniquement). */
import { lazy } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Layout } from './shell/Layout';
import { EmptyState, Button } from './components/ui';
// Flux quotidien (chemin critique) chargé d'emblée…
import { Dashboard } from './screens/Dashboard';
import { InvoicesList, InvoiceDetail, ReceivedList } from './screens/Invoices';
import { RecurringList } from './screens/Recurring';
import { CreateInvoice } from './screens/CreateInvoice';
// …écrans secondaires en code-splitting (chunks séparés, chargés à la demande).
// Plusieurs lazy() pointant le même module partagent un seul chunk (dédup Vite).
const ReceivedDetail = lazy(() => import('./screens/Suppliers').then((m) => ({ default: m.ReceivedDetail })));
const SuppliersList = lazy(() => import('./screens/Suppliers').then((m) => ({ default: m.SuppliersList })));
const SupplierDetail = lazy(() => import('./screens/Suppliers').then((m) => ({ default: m.SupplierDetail })));
const QuotesList = lazy(() => import('./screens/Quotes').then((m) => ({ default: m.QuotesList })));
const QuoteDetail = lazy(() => import('./screens/Quotes').then((m) => ({ default: m.QuoteDetail })));
const CatalogList = lazy(() => import('./screens/Catalog').then((m) => ({ default: m.CatalogList })));
const TemplatesList = lazy(() => import('./screens/Templates').then((m) => ({ default: m.TemplatesList })));
const ClientsList = lazy(() => import('./screens/Misc').then((m) => ({ default: m.ClientsList })));
const ClientDetail = lazy(() => import('./screens/Misc').then((m) => ({ default: m.ClientDetail })));
const ClientForm = lazy(() => import('./screens/Misc').then((m) => ({ default: m.ClientForm })));
const ConformityPage = lazy(() => import('./screens/Misc').then((m) => ({ default: m.ConformityPage })));
const Notifications = lazy(() => import('./screens/Misc').then((m) => ({ default: m.Notifications })));
const Admin = lazy(() => import('./screens/Admin').then((m) => ({ default: m.Admin })));
const Ereporting = lazy(() => import('./screens/Ereporting').then((m) => ({ default: m.Ereporting })));
const Settings = lazy(() => import('./screens/Settings').then((m) => ({ default: m.Settings })));
const Team = lazy(() => import('./screens/Account').then((m) => ({ default: m.Team })));
const Subscription = lazy(() => import('./screens/Account').then((m) => ({ default: m.Subscription })));
const Support = lazy(() => import('./screens/Account').then((m) => ({ default: m.Support })));
const Privacy = lazy(() => import('./screens/Legal').then((m) => ({ default: m.Privacy })));
const MandateScreen = lazy(() => import('./screens/Mandate').then((m) => ({ default: m.MandateScreen })));
const Cotisations = lazy(() => import('./screens/Cotisations').then((m) => ({ default: m.Cotisations })));
import { RequireSession } from './auth/RequireSession';
import Connexion from './auth/Connexion';
import Inscription from './auth/Inscription';
import { OubliMotDePasse, Reinitialiser } from './auth/MotDePasseOublie';
import VerifEmail from './auth/VerifEmail';
import Onboarding from './auth/Onboarding';
import Invitation from './auth/Invitation';

/* Page 404 : toute URL inconnue sous le shell. Ce point de chute affichait
   « Écran en cours de portage depuis le prototype », message destiné aux
   développeurs et visible par les clients (constat d'audit UX QW5). */
function NotFound() {
  const navigate = useNavigate();
  return (
    <EmptyState
      glyph="search"
      id="404"
      title="Cette page n’existe pas"
      actions={
        <Button variant="primary" icon="grid" onClick={() => navigate('/dashboard')}>
          Retour au tableau de bord
        </Button>
      }
    >
      L’adresse demandée ne correspond à aucun écran de Factumis. Le lien est peut-être erroné, ou la page a été déplacée.
    </EmptyState>
  );
}

export function App() {
  return (
    <Routes>
      {/* Routes publiques — hors Layout, hors garde « org requise ».
          /onboarding est protégée par la session (un user non connecté qui y
          accède sera renvoyé /connexion par Onboarding/les appels API) mais
          PAS par la garde org (sinon boucle), car c'est là qu'on crée l'org. */}
      <Route path="/connexion" element={<Connexion />} />
      <Route path="/inscription" element={<Inscription />} />
      <Route path="/oubli-mot-de-passe" element={<OubliMotDePasse />} />
      <Route path="/reinitialiser" element={<Reinitialiser />} />
      <Route path="/verifier-email" element={<VerifEmail />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/invitation/:id" element={<Invitation />} />

      {/* Arbre de l'app — protégé : session + organisation active requises. */}
      <Route element={<RequireSession />}>
        <Route path="/" element={<Layout />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="cotisations" element={<Cotisations />} />
        <Route path="factures" element={<InvoicesList />} />
        <Route path="factures/nouvelle" element={<CreateInvoice />} />
        <Route path="factures/:id" element={<InvoiceDetail />} />
        <Route path="recues" element={<ReceivedList />} />
        <Route path="recues/:id" element={<ReceivedDetail />} />
        <Route path="factures-recurrentes" element={<RecurringList />} />
        <Route path="devis" element={<QuotesList />} />
        <Route path="devis/:id" element={<QuoteDetail />} />
        <Route path="catalogue" element={<CatalogList />} />
        <Route path="modeles" element={<TemplatesList />} />
        <Route path="clients" element={<ClientsList />} />
        <Route path="clients/nouveau" element={<ClientForm />} />
        <Route path="clients/modifier/:id" element={<ClientForm />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="fournisseurs" element={<SuppliersList />} />
        <Route path="fournisseurs/:id" element={<SupplierDetail />} />
        <Route path="e-reporting" element={<Ereporting />} />
        {/* Alias sans tiret : l'URL /ereporting tombait sur le placeholder générique
            « en cours de portage » (constat recette 2026-07-15). */}
        <Route path="ereporting" element={<Navigate to="/e-reporting" replace />} />
        <Route path="conformite" element={<ConformityPage />} />
        <Route path="parametres" element={<Settings />} />
        <Route path="equipe" element={<Team />} />
        <Route path="abonnement" element={<Subscription />} />
        <Route path="support" element={<Support />} />
        <Route path="confidentialite" element={<Privacy />} />
        <Route path="notifications" element={<Notifications />} />
        {/* Mandat de representation : hors menu, atteint depuis Parametres/onboarding. */}
        <Route path="mandat" element={<MandateScreen />} />
          {/* Route opérateur — hors menu, accès par URL directe uniquement (Lot 6). */}
          <Route path="admin" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
